import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { createManifestExport, downloadManifestExport, getManifestExport } from './manifestExport';

const STATUSES = ['pending', 'processing', 'completed', 'failed'];
const POLL_DELAY = 2500;
const MAX_FAILURES = 5;

function storageKey() {
  return `manifest-export:${process.env.REACT_APP_DJANGO_API_BASE_URL || ''}:${localStorage.getItem('username') || ''}`;
}

function readJob() {
  if (!localStorage.getItem('token')) return null;
  try {
    const job = JSON.parse(sessionStorage.getItem(storageKey()));
    return job?.id && job.start_date && job.end_date && STATUSES.includes(job.status) ? job : null;
  } catch (_) { return null; }
}

function persistJob(job) {
  try {
    if (job) {
      // Do not persist auth tokens or potentially short-lived signed URLs.
      const { id, start_date, end_date, status, progress } = job;
      sessionStorage.setItem(storageKey(), JSON.stringify({ id, start_date, end_date, status, progress }));
    } else sessionStorage.removeItem(storageKey());
  } catch (_) { /* Storage may be disabled; the current popup still works. */ }
}

export default function useManifestExport() {
  const [job, setJob] = useState(readJob);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [pollError, setPollError] = useState('');
  const [pollPaused, setPollPaused] = useState(false);
  const [pollVersion, setPollVersion] = useState(0);
  const alive = useRef(false);
  const createController = useRef(null);
  const downloadController = useRef(null);
  const downloadUrls = useRef(new Map());
  const notified = useRef(job && ['completed', 'failed'].includes(job.status) ? `${job.id}:${job.status}` : '');

  useEffect(() => {
    alive.current = true;
    const urls = downloadUrls.current;
    return () => {
      alive.current = false;
      createController.current?.abort();
      downloadController.current?.abort();
      urls.forEach((timer, url) => { clearTimeout(timer); URL.revokeObjectURL(url); });
      urls.clear();
    };
  }, []);

  const jobId = job?.id;
  useEffect(() => {
    if (!jobId) return undefined;
    const controller = new AbortController();
    let timer;
    let failures = 0;
    setPollPaused(false);
    setPollError('');

    const poll = async () => {
      try {
        const current = await getManifestExport(jobId, controller.signal);
        if (controller.signal.aborted) return;
        if (current.id !== jobId || !STATUSES.includes(current.status)) throw new Error('The export service returned an invalid status.');
        failures = 0;
        setPollError('');
        setJob(previous => {
          const updated = { ...previous, ...current };
          persistJob(updated);
          return updated;
        });
        if (['completed', 'failed'].includes(current.status)) {
          const notice = `${jobId}:${current.status}`;
          if (notified.current !== notice) {
            notified.current = notice;
            if (current.status === 'completed') toast.success('Export ready. Choose Download CSV when you are ready.');
            else toast.error(current.error || 'Export failed. Please try again.');
          }
          return;
        }
        timer = setTimeout(poll, POLL_DELAY);
      } catch (failure) {
        if (controller.signal.aborted) return;
        failures += 1;
        if ([401, 403, 404, 410].includes(failure.status) || failures >= MAX_FAILURES) {
          setPollPaused(true);
          setPollError(`${failure.message || 'Unable to check export status.'} Status checks are paused; your export has not been cancelled.`);
          return;
        }
        setPollError('Connection interrupted. Retrying the status check… Your export may still be running.');
        timer = setTimeout(poll, Math.min(POLL_DELAY * 2 ** failures, 30000));
      }
    };

    poll(); // Refresh even a stored completed job, since the file may have expired.
    return () => { controller.abort(); clearTimeout(timer); };
  }, [jobId, pollVersion]);

  const start = useCallback(async (startDate, endDate) => {
    if (createController.current || (job && ['pending', 'processing'].includes(job.status))) return;
    const controller = new AbortController();
    createController.current = controller; // Immediate guard, before React rerenders.
    setCreating(true);
    setError('');
    try {
      const result = await createManifestExport({ start_date: startDate, end_date: endDate }, controller.signal);
      if (controller.signal.aborted || !alive.current) return;
      if (!result.id || !STATUSES.includes(result.status)) throw new Error('The export service returned an invalid job.');
      const next = { ...result, start_date: startDate, end_date: endDate };
      persistJob(next);
      setJob(next);
    } catch (failure) {
      if (!controller.signal.aborted && alive.current) {
        const message = failure.message || 'Could not start the export. Please try again.';
        setError(message);
        toast.error(message);
      }
    } finally {
      createController.current = null;
      if (alive.current) setCreating(false);
    }
  }, [job]);

  const download = useCallback(async () => {
    if (downloadController.current || job?.status !== 'completed') return;
    const controller = new AbortController();
    downloadController.current = controller;
    setDownloading(true);
    setError('');
    try {
      const result = await downloadManifestExport(job, controller.signal);
      if (controller.signal.aborted || !alive.current) return;
      const url = URL.createObjectURL(result.blob);
      const timer = setTimeout(() => { URL.revokeObjectURL(url); downloadUrls.current.delete(url); }, 1000);
      downloadUrls.current.set(url, timer);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      try { link.click(); } finally { link.remove(); }
    } catch (failure) {
      if (!controller.signal.aborted && alive.current) {
        const message = failure.message || 'Could not download the CSV. Please try again.';
        setError(message);
        toast.error(message);
      }
    } finally {
      downloadController.current = null;
      if (alive.current) setDownloading(false);
    }
  }, [job]);

  const reset = () => { persistJob(null); setJob(null); setError(''); setPollError(''); setPollPaused(false); };
  const checkAgain = () => setPollVersion(version => version + 1);
  return { job, creating, downloading, error, pollError, pollPaused, start, download, reset, checkAgain };
}
